import { selectEditMode, selectVisaAndSalaryRange } from '../../../../store/signUp/selectors';
import { setSignUpStep, setVisaAndSalaryRange } from '../../../../store/signUp/slice';
import { validateNumberRange } from '../../../../utils/generalHelpers';
import { useDispatch, useSelector } from '../../../../store/store';
import MotionWrapper from '../../../../containers/MotionWrapper';
import RadioButton from '../../../../components/RadioButton';
import { Dispatch, SetStateAction, useState } from 'react';
import Button from '../../../../components/Button';
import Input from '../../../../components/Input';
import styles from './style.module.scss';
import DropBox from '../../../../containers/DropBox';

interface Props {
    resumeFile: File | null;
    portfolioFile: File | null;
    coverLetterFile: File | null;
    certificateFile: File | null;
    setResumeFile: Dispatch<SetStateAction<File | null>>;
    setPortfolioFile: Dispatch<SetStateAction<File | null>>;
    setCoverLetterFile: Dispatch<SetStateAction<File | null>>;
    setCertificateFile: Dispatch<SetStateAction<File | null>>;
};

function UploadFiles(props: Props) {
    const {
        resumeFile,
        portfolioFile,
        setResumeFile,
        coverLetterFile,
        certificateFile,
        setPortfolioFile,
        setCoverLetterFile,
        setCertificateFile,
    } = props;

    const editMode = useSelector(selectEditMode);
    const visaAndSalary = useSelector(selectVisaAndSalaryRange);
    const [salaryRange, setSalaryRange] = useState(visaAndSalary?.salaryRange || '');
    const [sponsorship, setSponsorship] = useState(visaAndSalary?.requireSponsorshipVisa || 'not-set');
    const [isSalaryRangeValid, setIsSalaryRangeValid] = useState(validateNumberRange(visaAndSalary?.salaryRange || ''));
    const dispatch = useDispatch();

    const next = () => {
        const formData = {
            salaryRange,
            requireSponsorshipVisa: sponsorship,
        };

        dispatch(setVisaAndSalaryRange(formData));
        dispatch(setSignUpStep('reviewInfo'));
    };

    return (
        <MotionWrapper className={styles.wrapper}>
            <div className={styles.grid}>
                <DropBox
                    isRequired
                    label='Upload resume (PDF or Word file)'
                    fileTypes={{ 'application/pdf': ['.pdf'], 'application/doc': ['.doc', '.docx'] }}
                    file={resumeFile}
                    setFile={setResumeFile}
                    uploading={false}
                    description='PDF, Doc or Docx , Maximum upload file size: 12MB'
                />
                <DropBox
                    label='Upload portfolio (Optional)'
                    fileTypes={{ 'application/pdf': ['.pdf'], 'application/doc': ['.doc', '.docx'] }}
                    file={portfolioFile}
                    setFile={setPortfolioFile}
                    uploading={false}
                    description='PDF, Doc or Docx , Maximum upload file size: 12MB'
                />
                <DropBox
                    label='Upload cover letter (Optional)'
                    fileTypes={{ 'application/pdf': ['.pdf'], 'application/doc': ['.doc', '.docx'] }}
                    file={coverLetterFile}
                    setFile={setCoverLetterFile}
                    uploading={false}
                    description='PDF, Doc or Docx , Maximum upload file size: 12MB'
                />
                <DropBox
                    label='Upload certificate (Optional)'
                    fileTypes={{ 'application/pdf': ['.pdf'], 'application/doc': ['.doc', '.docx'] }}
                    file={certificateFile}
                    setFile={setCertificateFile}
                    uploading={false}
                    description='PDF, Doc or Docx , Maximum upload file size: 12MB'
                />
            </div>
            <Input
                isRequired
                label='What is your salary expectation?'
                placeholder='Ex: 40000-55000'
                type='text'
                validation='NumberRange'
                value={salaryRange}
                setIsValid={setIsSalaryRangeValid}
                onChange={setSalaryRange}
            />
            <div className={styles.sponsorship}>
                <p>
                    Will you now, or in the future, require
                    sponsorship for employment visa status
                    in the USA (e.g. H-1B visa status)? <b>*</b>
                </p>
                <div className={styles.sponsorship_radio}>
                    <RadioButton
                        text='Yes'
                        checked={sponsorship === 'Yes'}
                        setChecked={() => setSponsorship('Yes')}
                    />
                    <RadioButton
                        text='No'
                        checked={sponsorship === 'No'}
                        setChecked={() => setSponsorship('No')}
                    />
                </div>
            </div>
            <div className={styles.actions}>
                <Button
                    size='sm'
                    text='Back'
                    type='Outline-Dark'
                    className={styles.actions_btn}
                    onClick={() => dispatch(setSignUpStep('expertise'))}
                />
                <Button
                    size='sm'
                    type='Primary'
                    onClick={next}
                    className={styles.actions_btn}
                    text={editMode ? 'Save' : 'Continue'}
                    disabled={
                        !resumeFile ||
                        !salaryRange ||
                        !isSalaryRangeValid ||
                        sponsorship === 'not-set'
                    } />
            </div>
        </MotionWrapper>
    );
};

export default UploadFiles;
